import {
  Button,
  EmptyState,
  Modal,
  PageHeader,
  Table,
  UserInfo,
  createExitHold,
  useConfirm,
  useToast,
} from '@/components';
import { ChangeOwnerModal } from '@/features/apple-udids/ChangeOwnerModal';
import { UdidStatusBadge } from '@/features/apple-udids/UdidStatusBadge';
import {
  useAllAppleUdids,
  useImportFadAppleUdids,
  useRegisterAppleUdids,
  useVerifyAppleUdids,
} from '@/features/apple-udids/hooks';
import { useReleaseIntegrations } from '@/features/release-config/hooks';
import { formatDateTime } from '@/lib/datetime';
import { type AdminAppleUdid, ApiClientError } from '@uchat-team-management/shared';
import { For, Show, createSignal } from 'solid-js';

type ImportSummary = {
  imported: number;
  skipped: number;
  unmatched: { email: string; model: string }[];
};

type VerifySummary = {
  verified: number;
  missing: {
    udid: string;
    label: string;
    user: { id: string; displayName: string; email: string };
  }[];
  portalOnly: number;
};

// Release > Apple UDID (UCH-426): every user's device UDIDs with registration
// state; Register dispatches the register-ios-devices workflow in the mobile
// repo and marks the pending rows.
export default function ReleaseAppleUdids() {
  const udids = useAllAppleUdids();
  const register = useRegisterAppleUdids();
  const importFad = useImportFadAppleUdids();
  const verify = useVerifyAppleUdids();
  const integrations = useReleaseIntegrations();
  const toast = useToast();
  const confirmDialog = useConfirm();
  const [importSummary, setImportSummary] = createSignal<ImportSummary | null>(null);
  const [verifySummary, setVerifySummary] = createSignal<VerifySummary | null>(null);
  const [changingOwner, setChangingOwner] = createSignal<AdminAppleUdid | null>(null);
  const changingHeld = createExitHold(changingOwner);

  const rows = () => udids.data?.data ?? [];
  const pendingCount = () => rows().filter((r) => r.registeredAt === null).length;

  const onRegister = async () => {
    const ok = await confirmDialog({
      title: `Register ${pendingCount()} pending device(s) with Apple?`,
      body: 'Dispatches the register-ios-devices workflow; the new devices are only usable from the next ad hoc build.',
      confirmLabel: 'Register',
    });
    if (!ok) return;
    try {
      const resp = await register.mutateAsync();
      toast.success(`Dispatched registration for ${resp.data.dispatched} device(s)`);
    } catch (err) {
      if (err instanceof ApiClientError) toast.error(err.error.message);
      else toast.error('Failed to dispatch registration');
    }
  };

  const onImport = async () => {
    const ok = await confirmDialog({
      title: 'Import tester UDIDs from App Distribution?',
      body: 'Fetches every UDID App Distribution collected from testers who accepted an iOS invite and adds the ones matching a user by email (primary or "Other emails" in their Personal settings).',
      confirmLabel: 'Import',
    });
    if (!ok) return;
    try {
      const resp = await importFad.mutateAsync();
      setImportSummary(resp.data);
    } catch (err) {
      if (err instanceof ApiClientError) toast.error(err.error.message);
      else toast.error('Failed to import from App Distribution');
    }
  };

  const onVerify = async () => {
    const ok = await confirmDialog({
      title: 'Verify devices with Apple?',
      body: 'Checks every UDID against the App Store Connect device list. Devices missing from the portal go back to Pending — a just-dispatched registration takes a few minutes, so verifying too early moves those back too (re-registering them later is safe).',
      confirmLabel: 'Verify',
    });
    if (!ok) return;
    try {
      const resp = await verify.mutateAsync();
      setVerifySummary(resp.data);
    } catch (err) {
      if (err instanceof ApiClientError) toast.error(err.error.message);
      else toast.error('Failed to verify with Apple');
    }
  };

  const columns = [
    {
      key: 'user',
      header: 'User',
      render: (r: AdminAppleUdid) => <UserInfo user={r.user} />,
    },
    {
      key: 'label',
      header: 'Device',
      render: (r: AdminAppleUdid) => <span>{r.label}</span>,
    },
    {
      key: 'udid',
      header: 'UDID',
      render: (r: AdminAppleUdid) => <span class="font-mono text-xs">{r.udid}</span>,
    },
    {
      key: 'registered',
      header: 'Registered',
      render: (r: AdminAppleUdid) => (
        <div class="flex flex-col gap-0.5">
          <UdidStatusBadge row={r} />
          <Show when={r.verifiedAt ?? r.registeredAt}>
            {(at) => (
              <span class="text-neutral-500 text-xs dark:text-neutral-400">
                {formatDateTime(at())}
                {r.registeredBy ? ` by ${r.registeredBy.displayName}` : ''}
              </span>
            )}
          </Show>
        </div>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right' as const,
      render: (r: AdminAppleUdid) => (
        <Button size="sm" variant="ghost" onClick={() => setChangingOwner(r)}>
          Change user
        </Button>
      ),
    },
  ];

  return (
    <section>
      <PageHeader
        title="Apple UDID"
        sub="Device identifiers for iOS ad hoc builds"
        actions={
          <div class="flex gap-2">
            <Button
              variant="secondary"
              disabled={!integrations.data?.data.appStoreConnect}
              loading={verify.isPending}
              title={
                integrations.data?.data.appStoreConnect
                  ? undefined
                  : 'App Store Connect is not configured'
              }
              onClick={() => void onVerify()}
            >
              Verify with Apple
            </Button>
            <Button
              variant="secondary"
              loading={importFad.isPending}
              onClick={() => void onImport()}
            >
              Import from App Distribution
            </Button>
            <Button
              disabled={pendingCount() === 0}
              loading={register.isPending}
              onClick={() => void onRegister()}
            >
              Register {pendingCount()} pending device(s)
            </Button>
          </div>
        }
      />
      <div class="mx-auto max-w-6xl p-6">
        <Table
          data={rows()}
          columns={columns}
          rowKey={(r) => r.id}
          loading={udids.isLoading}
          empty={
            <EmptyState
              title="No UDIDs yet"
              body="Users add their device UDIDs in their Personal settings."
            />
          }
        />
      </div>

      <Modal
        open={importSummary() !== null}
        onClose={() => setImportSummary(null)}
        title="App Distribution import"
      >
        <Show when={importSummary()} keyed>
          {(summary) => (
            <div class="flex flex-col gap-4 text-sm">
              <p class="text-neutral-700 dark:text-neutral-200">
                Imported <strong>{summary.imported}</strong> new device(s), skipped{' '}
                <strong>{summary.skipped}</strong> already in the registry.
              </p>
              <Show when={summary.unmatched.length > 0}>
                <div class="flex flex-col gap-2">
                  <p class="text-neutral-700 dark:text-neutral-200">
                    {summary.unmatched.length} tester(s) matched no user. Ask them to add that email
                    under “Other emails” in their Personal settings, then import again:
                  </p>
                  <ul class="flex flex-col divide-y divide-neutral-100 rounded-lg border border-neutral-100 dark:divide-neutral-800 dark:border-neutral-800">
                    <For each={summary.unmatched}>
                      {(t) => (
                        <li class="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
                          <span class="font-medium text-neutral-900 dark:text-neutral-100">
                            {t.email}
                          </span>
                          <span class="text-neutral-500 text-xs dark:text-neutral-400">
                            {t.model}
                          </span>
                        </li>
                      )}
                    </For>
                  </ul>
                </div>
              </Show>
              <div class="flex justify-end">
                <Button onClick={() => setImportSummary(null)}>Done</Button>
              </div>
            </div>
          )}
        </Show>
      </Modal>

      <Show when={changingHeld()}>
        {(row) => (
          <ChangeOwnerModal
            open={changingOwner() !== null}
            row={row()}
            onClose={() => setChangingOwner(null)}
          />
        )}
      </Show>

      <Modal
        open={verifySummary() !== null}
        onClose={() => setVerifySummary(null)}
        title="Apple portal verification"
      >
        <Show when={verifySummary()} keyed>
          {(summary) => (
            <div class="flex flex-col gap-4 text-sm">
              <p class="text-neutral-700 dark:text-neutral-200">
                <strong>{summary.verified}</strong> device(s) confirmed on the Apple portal
                {summary.portalOnly > 0
                  ? `; ${summary.portalOnly} portal device(s) are not tracked here.`
                  : '.'}
              </p>
              <Show when={summary.missing.length > 0}>
                <div class="flex flex-col gap-2">
                  <p class="text-neutral-700 dark:text-neutral-200">
                    {summary.missing.length} device(s) are not on the portal and went back to
                    Pending — include them in the next registration:
                  </p>
                  <ul class="flex flex-col divide-y divide-neutral-100 rounded-lg border border-neutral-100 dark:divide-neutral-800 dark:border-neutral-800">
                    <For each={summary.missing}>
                      {(m) => (
                        <li class="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
                          <span class="font-medium text-neutral-900 dark:text-neutral-100">
                            {m.user.displayName || m.user.email} · {m.label}
                          </span>
                          <span class="font-mono text-neutral-500 text-xs dark:text-neutral-400">
                            {m.udid}
                          </span>
                        </li>
                      )}
                    </For>
                  </ul>
                </div>
              </Show>
              <div class="flex justify-end">
                <Button onClick={() => setVerifySummary(null)}>Done</Button>
              </div>
            </div>
          )}
        </Show>
      </Modal>
    </section>
  );
}
